import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import './EmptyServiceCard.css';
import PhoneInput from './PhoneInput';
import CampoFechaDeNacimiento from './business/CampoFechaDeNacimiento';
import { API_URL } from '../config/api';
import { coberturaParticular, nombreDeObraSocial, PARTICULAR } from '../data/obrasSociales';


function EmptyServiceCard({
    bookingData,
    onConfirm,
    onCancel,
    isDemo = false,
    /**
     * ¿Pedir los datos de paciente? (DNI, fecha de nacimiento y cobertura)
     *
     * Lo decide BusinessPage y sale de que el negocio sea del rubro salud y haya
     * declarado con qué obras sociales atiende. Un consultorio los necesita para
     * facturarle a la obra social; una peluquería no tiene por qué pedir un DNI
     * ni una fecha de nacimiento para cortar el pelo, y son datos sensibles.
     */
    pideDatosDePaciente = false,
    coberturas = [],
    /** El slug del negocio, para buscar al paciente por DNI en SU agenda. */
    slugDelNegocio = null,
}) {
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    const [customerEmail, setCustomerEmail] = useState('');
    // Los tres de paciente. Sólo se piden —y sólo se validan— si
    // `pideDatosDePaciente`; en el resto de los negocios viajan vacíos.
    const [customerDni, setCustomerDni] = useState('');
    const [customerNacimiento, setCustomerNacimiento] = useState('');
    const [obraSocial, setObraSocial] = useState('');
    const [numeroDeAfiliado, setNumeroDeAfiliado] = useState('');
    /**
     * Estado de la búsqueda por DNI: 'buscando' | 'encontrado' | 'nuevo' | null.
     * Es lo que decide qué cartel se muestra debajo del campo.
     */
    const [busquedaDni, setBusquedaDni] = useState(null);
    const menosMovimiento = useReducedMotion();
    // Permiso para escribirle por WhatsApp. Va aparte del teléfono a propósito:
    // dar el número para que el negocio pueda contactarte NO es lo mismo que
    // autorizar mensajes automáticos, y Meta exige el consentimiento explícito
    // para mandar plantillas. Arranca en false: si el cliente no lo marca, no se
    // le escribe.
    const [aceptaWhatsApp, setAceptaWhatsApp] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    /**
     * Al cambiar de horario se limpia el ERROR, no la persona.
     *
     * Acá se borraban también el nombre, el teléfono y el mail. El efecto
     * depende de `bookingData`, que cambia de identidad cada vez que se elige
     * otro día u otra hora — así que cambiar de horario después de haber
     * completado los datos los borraba todos. Con el DNI eso quedó todavía peor:
     * el DNI y la cobertura NO estaban en esta lista, así que el formulario
     * quedaba abierto y a medio llenar, como si se hubiera roto.
     *
     * Cambiar de horario no cambia quién sos. Lo único que deja de valer es un
     * error de validación anterior, que puede ser de un horario que ya no está
     * elegido.
     */
    useEffect(() => {
        if (bookingData) {
            setError('');
            setIsLoading(false);
            
            // 🔍 DEBUG: Verificar datos de precio dinámico
            // console.log('💰 EmptyServiceCard - Datos recibidos:', {
            //     serviceName: bookingData.service?.name,
            //     basePrice: bookingData.service?.price,
            //     dynamicPrice: bookingData.dynamicPrice,
            //     priceMultiplier: bookingData.priceMultiplier,
            //     enableDynamicPricing: bookingData.service?.enableDynamicPricing,
            //     priceVariations: bookingData.service?.priceVariations?.length || 0,
            //     date: bookingData.date,
            //     time: bookingData.time
            // });
        }
    }, [bookingData]);

    if (!bookingData) {
        return null;
    }

    /**
     * ¿Hay que pedir el número de afiliado? Sólo con una cobertura real elegida.
     * "Particular" no tiene número, así que pedírselo sería un campo obligatorio
     * que esa persona no puede completar.
     */
    const pidenAfiliado = Boolean(obraSocial) && obraSocial !== PARTICULAR;

    /**
     * ¿Ya se puede mostrar el resto del formulario?
     *
     * En los negocios que piden datos de paciente, la pantalla arranca con el
     * DNI solo. El resto aparece recién cuando ese DNI se consultó —haya ficha o
     * no—, y por eso alcanza con que `busquedaDni` tenga cualquier resultado.
     *
     * Es a propósito que no se muestre todo de una: quien ya vino sólo escribe
     * ocho dígitos y revisa, en vez de volver a tipear cinco campos que el
     * consultorio ya tiene. Y quien viene por primera vez pierde un paso, no
     * cinco.
     *
     * `'buscando'` no alcanza: mostrar los campos vacíos mientras la respuesta
     * viaja hace que alguien empiece a escribir el nombre y se lo pisemos.
     *
     * En los negocios que NO piden datos de paciente no hay DNI ni etapas: el
     * formulario es el de siempre y se muestra entero.
     */
    const mostrarResto =
        !pideDatosDePaciente || busquedaDni === 'encontrado' || busquedaDni === 'nuevo';

    // DIAGNÓSTICO TEMPORAL — sacar cuando esté resuelto.
    console.log('[OS-2 EmptyServiceCard] props', {
      pideDatosDePaciente,
      cuantasCoberturas: coberturas.length,
      slugDelNegocio,
    });

    /**
     * Busca al paciente por DNI y completa el resto del formulario.
     *
     * La dispara el botón "Siguiente" (o Enter, que es lo mismo), nunca sola.
     * No corre en cada tecla por dos motivos: escribiendo ocho dígitos serían
     * ocho pedidos de los cuales siete van a un DNI incompleto, y el backend
     * permite seis por minuto. Y tampoco corre al salir del campo, porque
     * autocompletar media pantalla porque alguien tocó en otro lado deja a esa
     * persona mirando cinco campos que se llenaron sin que los pidiera.
     *
     * Nunca pisa lo que la persona ya escribió: si alguien empezó por el nombre y
     * después puso el DNI, lo suyo manda. Lo encontrado sólo rellena huecos, y
     * arriba queda el cartel para que confirme que está todo bien —que es el
     * punto: se completa solo, pero lo revisa igual antes de reservar.
     */
    const buscarPorDni = async () => {
        const limpio = customerDni.replace(/\./g, '').trim();
        if (!pideDatosDePaciente || !slugDelNegocio || !/^\d{7,8}$/.test(limpio)) {
            setBusquedaDni(null);
            return;
        }

        setBusquedaDni('buscando');
        console.log('[OS-3 buscarPorDni] pidiendo', `${API_URL}/api/public/business/${slugDelNegocio}/paciente?dni=${limpio}`);
        try {
            const res = await fetch(
                `${API_URL}/api/public/business/${slugDelNegocio}/paciente?dni=${limpio}`
            );
            const data = await res.json();
            console.log('[OS-3 buscarPorDni] respuesta', res.status, data);
            if (!res.ok) throw new Error('falló la búsqueda');

            if (!data.encontrado) {
                setBusquedaDni('nuevo');
                return;
            }

            const p = data.paciente || {};
            setCustomerName((actual) => actual || p.name || '');
            setCustomerEmail((actual) => actual || p.email || '');
            setCustomerPhone((actual) => actual || p.phone || '');
            setCustomerNacimiento((actual) => actual || p.fechaDeNacimiento || '');
            setObraSocial((actual) => actual || p.obraSocial || '');
            setNumeroDeAfiliado((actual) => actual || p.numeroDeAfiliado || '');
            setBusquedaDni('encontrado');
        } catch (e) {
            console.log('[OS-3 buscarPorDni] ERROR', e.message);
            // En silencio: no encontrar los datos no impide reservar, sólo obliga
            // a escribirlos. Un cartel de error acá asustaría por algo que es una
            // comodidad, no un requisito.
            setBusquedaDni(null);
        }
    };

    /**
     * Los datos del cliente, armados en un solo lugar.
     *
     * Estaba escrito tres veces —reserva directa, demo y seña—, así que agregar
     * un campo obligaba a acordarse de las tres: era exactamente la forma de que
     * el DNI viajara al reservar normal y no al reservar con seña.
     *
     * Los tres de paciente sólo se mandan si se pidieron. En un negocio que no
     * los pide van en `undefined` y no quedan como claves vacías en el turno.
     */
    const datosDelCliente = () => ({
        name: customerName,
        phone: customerPhone,
        email: customerEmail,
        aceptaWhatsApp,
        ...(pideDatosDePaciente
            ? {
                  dni: customerDni.replace(/\./g, '').trim(),
                  fechaDeNacimiento: customerNacimiento,
                  obraSocial,
                  // Sólo si corresponde: un particular no manda un afiliado vacío.
                  ...(pidenAfiliado ? { numeroDeAfiliado: numeroDeAfiliado.trim() } : {}),
              }
            : {}),
    });

    const handleConfirm = async () => {
        // DIAGNÓSTICO TEMPORAL — sacar cuando esté resuelto. Esto es lo que se
        // manda de verdad: si acá no aparece `dni`, el problema es que
        // `pideDatosDePaciente` llegó en false.
        console.log('[OS-4 handleConfirm] customer que se envía →', datosDelCliente());

        if (!customerName || !customerPhone || !customerEmail) {
            setError('Por favor, completá todos los campos.');
            return;
        }

        // El teléfono, aparte y con su propio mensaje. `customerPhone` puede no
        // estar vacío y aun así no tener número: PhoneInput arma el valor con el
        // prefijo del país, así que "+54" solo pasa el control de arriba. Es la
        // única vía que tiene el negocio para avisar de un cambio de horario, y
        // es sobre este número que se manda el recordatorio.
        if (!/\d/.test(customerPhone.replace(/^\+?\d{1,3}/, ''))) {
            setError('Ingresá tu número de teléfono: es por donde te avisamos si hay un cambio.');
            return;
        }

        // Limpiar el número de teléfono para validación (remover espacios, + y guiones)
        const cleanPhone = customerPhone.replace(/[\s+-]/g, '');
        
        // Validar que tenga entre 10 y 15 dígitos (incluyendo código de país)
        if (!/^\d{10,15}$/.test(cleanPhone)) {
            setError('Por favor, ingresá un número de teléfono válido.');
            return;
        }

        if (!/\S+@\S+\.\S+/.test(customerEmail)) {
            setError('Por favor, ingresá un email válido.');
            return;
        }

        if (pideDatosDePaciente) {
            // Entre 7 y 8 dígitos, sin puntos: es lo que mide un DNI argentino.
            // Se limpian los puntos antes de validar porque mucha gente los
            // escribe, y rechazar "12.345.678" por el formato sería pedirle a
            // alguien que corrija algo que está bien.
            const dniLimpio = customerDni.replace(/\./g, '').trim();
            if (!/^\d{7,8}$/.test(dniLimpio)) {
                setError('Ingresá tu DNI, sin puntos.');
                return;
            }

            if (!customerNacimiento) {
                setError('Ingresá tu fecha de nacimiento.');
                return;
            }
            // Una fecha futura es siempre un error de tipeo, y sin este control
            // el turno queda con un paciente que todavía no nació.
            if (customerNacimiento > new Date().toISOString().slice(0, 10)) {
                setError('La fecha de nacimiento no puede ser futura.');
                return;
            }

            if (!obraSocial) {
                setError('Elegí tu obra social, o "Particular" si no tenés.');
                return;
            }

            if (pidenAfiliado && !numeroDeAfiliado.trim()) {
                setError(`Ingresá tu número de afiliado de ${nombreDeObraSocial(obraSocial, coberturas)}.`);
                return;
            }
        }

        setError('');
        setIsLoading(true);

        // En la demo no hay nada que guardar en el servidor: el turno se guarda
        // localmente en BusinessPage. Este componente posteaba a /api/bookings
        // siempre, y como el negocio de demostración no existe en la base, la
        // llamada fallaba y la reserva nunca se completaba.
        if (isDemo) {
            setIsLoading(false);
            onConfirm({
                ...bookingData,
                customer: datosDelCliente(),
            });
            return;
        }

        // 💰 ARREGLO: Si el servicio requiere seña, NO crear la reserva aquí
        // Solo pasar los datos al padre para que maneje el flujo de seña
        if (bookingData.service.requiresDeposit) {
            console.log('💰 Servicio requiere seña - NO creando reserva, solo pasando datos');
            
            const dataToSend = {
                ...bookingData,
                customer: datosDelCliente()
            };
            
            setIsLoading(false);
            onConfirm(dataToSend);
            return;
        }

        try {
            console.log('✅ Servicio SIN seña - creando reserva directamente');
            
            const payload = {
                businessId: bookingData.businessId,
                serviceName: bookingData.service.name,
                customerPhone: customerPhone,
                service: {
                    id: bookingData.service.id,
                    name: bookingData.service.name,
                    duration: bookingData.service.duration,
                    price: bookingData.service.price,
                    requireDeposit: bookingData.service.requireDeposit,
                    depositType: bookingData.service.depositType,
                    depositAmount: bookingData.service.depositAmount,
                    paymentMethods: bookingData.service.paymentMethods,
                    depositPolicy: bookingData.service.depositPolicy
                },
                date: bookingData.date.toISOString(),
                time: bookingData.time,
                /* `datosDelCliente()` y no el objeto escrito a mano que había
                   acá, que era `{ name, phone, aceptaWhatsApp, email }`.

                   ESTE ERA EL BUG de los datos de paciente, y el más difícil de
                   ver porque el componente YA tenía la función: se agregó para
                   no repetir el objeto en las tres ramas de `onConfirm`, pero
                   este POST —el camino de los servicios SIN seña, o sea el más
                   usado— seguía armándolo aparte. Así que el DNI, la fecha de
                   nacimiento, la obra social y el afiliado se completaban en
                   pantalla, se validaban, y se perdían justo antes de salir.

                   El síntoma era desconcertante: el formulario pedía todo bien,
                   la búsqueda por DNI funcionaba, el backend guardaba lo que le
                   llegaba, y sin embargo no había un solo turno con DNI. */
                customer: datosDelCliente(),
                /* El profesional y el lugar, con el nombre que usa el resto del
                   sistema. Acá iban como `staff` y `resource` planos, pero
                   `POST /api/bookings` leía `selectedStaff`/`selectedResource`,
                   así que toda reserva hecha por este camino —el de los
                   servicios SIN seña— se guardaba sin profesional y en el panel
                   el turno salía sin asignar.

                   El backend ahora acepta los dos nombres, pero se manda el
                   canónico igual: depender del alias es dejar la trampa armada
                   para el próximo que copie este objeto. */
                selectedStaff: bookingData.selectedStaff,
                selectedResource: bookingData.selectedResource,
                // Agregar información de seña si está disponible
                ...(bookingData.depositInfo && { depositInfo: bookingData.depositInfo }),
                // Agregar precio dinámico si está disponible
                ...(bookingData.dynamicPrice && { 
                    dynamicPrice: bookingData.dynamicPrice,
                    priceMultiplier: bookingData.priceMultiplier 
                })
            };

            // 🔍 DEBUG: Verificar qué se está enviando
            

    

            const response = await fetch(`${API_URL}/api/bookings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (response.status === 409) {
                const errorData = await response.json();
                setError(errorData.message || 'Este horario ya fue reservado.');
                setIsLoading(false);
                return;
            }
            
            if (!response.ok) {
                try {
                    const errorData = await response.json();
                    throw new Error(errorData.error || errorData.message || 'No se pudo confirmar la reserva. Intentá de nuevo.');
                } catch (jsonError) {
                    throw new Error('No se pudo confirmar la reserva. Intentá de nuevo.');
                }
            }

            const responseData = await response.json();

            // Si todo sale bien, llamamos a onConfirm para mostrar el modal de éxito
            const dataToSend = {
                ...bookingData,
                customer: datosDelCliente(),
                bookingId: responseData.bookingId,
                code: responseData.code, // AGREGAR EL CÓDIGO DE RESERVA
            };
            
            onConfirm(dataToSend);

        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    // Este turno no sale el precio de lista. Se compara contra el precio del
    // servicio y no contra `priceMultiplier`, que vale 1 tanto cuando no hay
    // franja cargada como cuando la franja cobra justo lo mismo.
    const precioEsDistinto =
        Boolean(bookingData.dynamicPrice) &&
        bookingData.dynamicPrice !== bookingData.service.price;

    return (
        <div className="empty-service-card">
            <div className="empty-card-header">
                <h3>Completá tu Reserva</h3>
                <button 
                    className="empty-card-close" 
                    onClick={onCancel}
                    disabled={isLoading}
                >
                    ×
                </button>
            </div>
            
            {error && <div className="empty-card-error">{error}</div>}
            
            <div className="empty-card-booking-info">
                <div className="booking-detail">
                    <span className="detail-icon">✂️</span>
                    <span className="detail-text">{bookingData.service.name}</span>
                </div>
                <div className="booking-detail">
                    <span className="detail-icon">📅</span>
                    <span className="detail-text">
                        {new Date(bookingData.date).toLocaleDateString("es-AR", {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                        })}
                    </span>
                </div>
                <div className="booking-detail">
                    <span className="detail-icon">🕐</span>
                    <span className="detail-text">{bookingData.time} hs</span>
                </div>
                {/* Con quién se va a atender.
                    En un consultorio esto es parte de lo que se está reservando,
                    no un detalle: quien pidió turno con una nutricionista tiene
                    que poder verificar acá que el turno quedó con ella. Antes la
                    tarjeta mostraba servicio, día, hora y precio, y el
                    profesional no aparecía en ningún lado del formulario que se
                    confirma. */}
                {bookingData.selectedStaff && !bookingData.selectedStaff.isAnyStaff && (
                    <div className="booking-detail">
                        <span className="detail-icon">👤</span>
                        <span className="detail-text">{bookingData.selectedStaff.name}</span>
                    </div>
                )}
                {bookingData.selectedResource && (
                    <div className="booking-detail">
                        <span className="detail-icon">📍</span>
                        <span className="detail-text">{bookingData.selectedResource.name}</span>
                    </div>
                )}
                <div className="booking-detail">
                    <span className="detail-icon">💰</span>
                    <span className="detail-text">
                        {precioEsDistinto ? (
                            <>
                                <span className="precio-turno">
                                    ${bookingData.dynamicPrice?.toLocaleString('es-AR')}
                                </span>
                                {/* El de lista tachado al lado, para que se vea contra
                                    qué cambió: el cliente lo acaba de leer en la lista
                                    de servicios y si no aparece piensa que se equivocó. */}
                                <span className="precio-turno__lista">
                                    ${bookingData.service.price?.toLocaleString('es-AR')}
                                </span>
                            </>
                        ) : (
                            <span>${bookingData.service.price?.toLocaleString('es-AR')}</span>
                        )}
                    </span>
                </div>

                {/* Por qué sale distinto.
                    Acá antes decía sólo "(+50%)", que informa cuánto pero no por
                    qué, y colgado de un precio más caro un porcentaje solo se lee
                    como recargo sorpresa. El motivo y la franja en la que rige lo
                    convierten en una regla del negocio, que es lo que es: si el
                    cliente quiere pagar el precio de lista, ahora sabe que le
                    alcanza con elegir otro horario. */}
                {precioEsDistinto && bookingData.motivoDelPrecio && (
                    <div className="booking-detail precio-turno__motivo">
                        <span className="detail-icon" aria-hidden="true">ℹ️</span>
                        <span className="detail-text">
                            <strong>{bookingData.motivoDelPrecio}</strong>
                            {bookingData.cuandoRigeElPrecio && (
                                <span className="precio-turno__cuando">
                                    {bookingData.cuandoRigeElPrecio}
                                </span>
                            )}
                        </span>
                    </div>
                )}
            </div>

            <div className="empty-card-form">
                {/* El DNI va PRIMERO cuando el negocio pide datos de paciente.
                    No es un capricho de orden: es la clave con la que se busca a
                    alguien que ya vino, así que preguntarlo antes es lo que
                    permite completarle el resto en vez de hacerle escribir todo
                    de nuevo. En un negocio que no pide datos de paciente este
                    campo no existe y el formulario arranca por el nombre. */}
                {pideDatosDePaciente && (
                    <div className="form-field form-field--ancho">
                        <label htmlFor="reserva-dni">DNI</label>
                        <div className="dni-fila">
                        <input
                            id="reserva-dni"
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            placeholder="12345678"
                            value={customerDni}
                            onChange={(e) => {
                                setCustomerDni(e.target.value);
                                // El cartel de la búsqueda anterior deja de valer
                                // apenas cambia el documento.
                                setBusquedaDni(null);
                            }}
                            // Sin `onBlur`: el que dispara la búsqueda es el
                            // botón de al lado, y nada más. Buscar al salir del
                            // campo autocompletaba media pantalla sin que nadie
                            // lo pidiera —basta con tocar en cualquier otro
                            // lado—, y eso deja a alguien mirando cinco campos
                            // que se llenaron solos sin entender qué los llenó.
                            // Enter sigue valiendo, que es apretar el botón.
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    buscarPorDni();
                                }
                            }}
                            disabled={isLoading}
                        />
                        {/* El único disparador de la búsqueda, al lado del campo.
                            Antes buscaba al salir del input y no había botón: se
                            veía un campo suelto y un confirmar apagado, sin nada
                            que indicara qué hacer, y el autocompletado saltaba
                            solo al tocar en cualquier otro lado.
                            Se muestra mientras falta buscar; con el resto del
                            formulario ya abierto no tiene nada que disparar. */}
                        {!mostrarResto && (
                            <button
                                type="button"
                                className="btn-buscar-dni"
                                onClick={buscarPorDni}
                                disabled={isLoading || busquedaDni === 'buscando'}
                            >
                                {busquedaDni === 'buscando' ? 'Buscando…' : 'Siguiente'}
                            </button>
                        )}
                        </div>
                        {busquedaDni === 'buscando' && (
                            <span className="dni-aviso">Buscando tus datos…</span>
                        )}
                        {busquedaDni === 'encontrado' && (
                            <span className="dni-aviso dni-aviso--ok">
                                Ya reservaste acá antes: completamos tus datos. Revisalos
                                por las dudas y cambiá lo que haga falta.
                            </span>
                        )}
                        {busquedaDni === 'nuevo' && (
                            <span className="dni-aviso">
                                Es tu primera vez acá. Completá el resto y listo.
                            </span>
                        )}
                    </div>
                )}

                {/* El resto del formulario se abre animado.
                    Va por framer y no por CSS a propósito: este componente
                    también se renderiza adentro de la vista previa del panel, y
                    ahí `.dashboard-container *` fija con !important qué puede
                    transicionar —el alto no está en la lista— y apaga las
                    animaciones. Framer escribe el estilo cuadro a cuadro, así
                    que no depende de eso. Es la misma decisión que PasoPlegable.

                    `height: auto` en el destino: framer lo mide y lo convierte a
                    píxeles. Con un `max-height` inventado, el bloque corto tarda
                    de más y el largo se corta.

                    El wrapper ocupa la fila entera y vuelve a ser grilla con las
                    mismas columnas: si fuera un div común, los campos pasarían a
                    ser hijos suyos y no de `.empty-card-form`, y las dos
                    columnas se convertirían en una. */}
                <AnimatePresence initial={false}>
                  {mostrarResto && (
                    <motion.div
                      key="resto-del-formulario"
                      className="empty-card-form__resto"
                      initial={menosMovimiento ? false : { height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={menosMovimiento ? undefined : { height: 0, opacity: 0 }}
                      transition={
                        menosMovimiento
                          ? { duration: 0 }
                          : { duration: 0.35, ease: [0.32, 0.72, 0, 1] }
                      }
                      style={{ overflow: 'hidden' }}
                    >
                <div className="form-field">
                    <label htmlFor="reserva-nombre">Nombre y apellido</label>
                    <input
                        id="reserva-nombre"
                        type="text"
                        autoComplete="name"
                        placeholder="Ingresá tu nombre completo"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        disabled={isLoading}
                    />
                </div>

                <div className="form-field">
                    <label htmlFor="reserva-email">Email</label>
                    <input
                        id="reserva-email"
                        type="email"
                        autoComplete="email"
                        placeholder="tu.email@ejemplo.com"
                        value={customerEmail}
                        onChange={(e) => setCustomerEmail(e.target.value)}
                        disabled={isLoading}
                    />
                </div>

                <div className="form-field">
                    <label>
                        WhatsApp <span className="campo-obligatorio">*</span>
                    </label>
                    <PhoneInput
                        value={customerPhone}
                        onChange={setCustomerPhone}
                        disabled={isLoading}
                        placeholder="Tu número sin código de área"
                        required
                    />
                </div>

                {pideDatosDePaciente && (
                    <div className="form-field">
                        <label htmlFor="reserva-nacimiento">Fecha de nacimiento</label>
                        {/* Era un `<input type="date">`: cada navegador lo dibuja
                            distinto y en el teléfono abre el selector del sistema.
                            El componente usa el calendario del sistema, con
                            desplegables de mes y año — para un cumpleaños, navegar
                            de a un mes no sirve. El valor sigue siendo el mismo
                            texto YYYY-MM-DD que valida `handleConfirm`. */}
                        <CampoFechaDeNacimiento
                            id="reserva-nacimiento"
                            value={customerNacimiento}
                            onChange={setCustomerNacimiento}
                            disabled={isLoading}
                        />
                    </div>
                )}

                {pideDatosDePaciente && (
                    <div className="form-field">
                        <label htmlFor="reserva-obra">Obra social</label>
                        <select
                            id="reserva-obra"
                            value={obraSocial}
                            onChange={(e) => {
                                setObraSocial(e.target.value);
                                // Al cambiar de cobertura el afiliado anterior no
                                // vale: es el número de OTRA obra social, y dejarlo
                                // puesto es mandarle al consultorio un dato que no
                                // corresponde con la que figura al lado.
                                setNumeroDeAfiliado('');
                            }}
                            disabled={isLoading}
                        >
                            <option value="">Elegí tu cobertura</option>
                            {coberturas.map((cobertura) => (
                                <option key={cobertura.id} value={cobertura.id}>
                                    {cobertura.nombre}
                                </option>
                            ))}
                            {/* Siempre al final y siempre presente: no sale de la
                                lista que arma el negocio. Sin esta opción, quien no
                                tiene cobertura no puede reservar. */}
                            <option value={coberturaParticular.id}>
                                {coberturaParticular.nombre} (sin cobertura)
                            </option>
                        </select>
                    </div>
                )}

                {/* El número de afiliado aparece recién cuando hay una obra social
                    elegida, y no con "Particular": un particular no tiene número,
                    así que pedírselo es un campo que no puede completar. */}
                {pideDatosDePaciente && pidenAfiliado && (
                    <div className="form-field">
                        <label htmlFor="reserva-afiliado">
                            Número de afiliado de {nombreDeObraSocial(obraSocial, coberturas)}
                        </label>
                        <input
                            id="reserva-afiliado"
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            placeholder="El que figura en tu credencial"
                            value={numeroDeAfiliado}
                            onChange={(e) => setNumeroDeAfiliado(e.target.value)}
                            disabled={isLoading}
                        />
                    </div>
                )}

                {/* El consentimiento va al final y ocupa el ancho: es una decisión
                    sobre todo lo de arriba, no un campo más de la grilla. */}
                <label className="consentimiento-whatsapp form-field--ancho">
                    <input
                        type="checkbox"
                        checked={aceptaWhatsApp}
                        onChange={(e) => setAceptaWhatsApp(e.target.checked)}
                        disabled={isLoading}
                    />
                    <span>Quiero recibir el recordatorio de mi turno por WhatsApp</span>
                </label>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Mientras no se consultó el DNI, la pantalla dice qué falta.
                    Sin esto queda un campo suelto y un botón de confirmar, y no
                    se entiende que hay que salir del campo para seguir. */}
                {!mostrarResto && busquedaDni !== 'buscando' && (
                    <p className="dni-aviso form-field--ancho">
                        Escribí tu DNI para continuar. Si ya reservaste acá, te
                        completamos el resto.
                    </p>
                )}
            </div>

            <div className="empty-card-actions">
                <button 
                    onClick={onCancel} 
                    className="btn-cancel-empty" 
                    disabled={isLoading}
                >
                    Cancelar
                </button>
                <button 
                    onClick={handleConfirm} 
                    className="btn-confirm-empty" 
                    // Además de mientras carga: hasta que el DNI no se consultó
                    // no hay campos para completar, así que confirmar ahí sólo
                    // podría producir un error de validación.
                    disabled={isLoading || !mostrarResto}
                >
                    {isLoading ? (
                        <>
                            <span className="spinner-empty"></span>
                            Confirmando...
                        </>
                    ) : (
                        'Confirmar Turno'
                    )}
                </button>
            </div>
        </div>
    );
}

export default EmptyServiceCard; 